// Care Library content — the GEO/SEO discovery engine. Each guide answers
// discrete questions ("is monstera toxic to cats") and is schema-markable.
// In production: Cosmos container careGuides, pk: /slug.

export const CARE_GUIDES = [
  {
    id: "monstera-deliciosa",
    slug: "monstera-deliciosa",
    plantName: "Monstera Deliciosa",
    summary:
      "How to keep a Monstera deliciosa thriving in an Irish home — light, watering through a damp winter, and the toxicity question every cat owner asks.",
    stats: { light: "Bright, indirect", water: "Weekly, let top inch dry", hardiness: "Indoor only (min 12°C)", toxicity: "Toxic to pets" },
    faq: [
      {
        q: "Is Monstera deliciosa toxic to cats and dogs?",
        a: "Yes. Monstera contains insoluble calcium oxalate crystals that irritate the mouth and stomach if chewed. Keep it out of reach of curious pets, or choose one of our pet-safe plants like Calathea or Pilea instead.",
      },
      {
        q: "How often should I water a Monstera in Ireland?",
        a: "Roughly once a week in summer, and every 10–14 days through the darker months. Let the top 2–3cm of compost dry before watering again. Irish homes are humid, so err on the side of under-watering — soggy roots are the number one killer.",
      },
      {
        q: "Why isn't my Monstera growing splits in its leaves?",
        a: "Fenestration (the holes) comes with maturity and good light. A Monstera in a dim corner will push out plain heart-shaped leaves. Move it closer to a bright window — but not into harsh direct sun — and new growth should start splitting.",
      },
      {
        q: "Will a Monstera cope with low winter light?",
        a: "It survives, but it won't grow much from November to February. That's normal. Don't feed it over winter, water less, and it'll pick straight back up come spring.",
      },
    ],
  },
  {
    id: "zz-raven",
    slug: "zz-raven",
    plantName: "ZZ Raven",
    summary:
      "The near-indestructible ZZ Raven and why it's the best plant for a dim, north-facing Irish room.",
    stats: { light: "Low to bright indirect", water: "Every 2–3 weeks", hardiness: "Indoor only (min 10°C)", toxicity: "Toxic to pets" },
    faq: [
      {
        q: "Can a ZZ plant survive in low light?",
        a: "Yes — this is its superpower. The ZZ Raven tolerates the kind of low, grey light that kills most houseplants, making it ideal for a hallway or north-facing midlands room. It grows faster in brighter spots but won't sulk in shade.",
      },
      {
        q: "How often does a ZZ plant need watering?",
        a: "Rarely. Every 2–3 weeks, and less in winter. It stores water in its rhizomes, so the most common way to kill one is overwatering. When in doubt, wait another week.",
      },
      {
        q: "Is the ZZ Raven safe around pets?",
        a: "No — like the standard ZZ, it's mildly toxic if chewed. If you have pets that nibble, pick from our pet-safe range instead.",
      },
    ],
  },
  {
    id: "calathea-orbifolia",
    slug: "calathea-orbifolia",
    plantName: "Calathea Orbifolia",
    summary:
      "A pet-safe statement plant that loves humidity — how to use an Irish bathroom to your advantage.",
    stats: { light: "Bright, indirect", water: "Keep lightly moist", hardiness: "Indoor only (min 15°C)", toxicity: "Pet safe" },
    faq: [
      {
        q: "Is Calathea orbifolia safe for cats and dogs?",
        a: "Yes — Calatheas are non-toxic to cats and dogs, which is why they're one of our most-recommended plants for pet households.",
      },
      {
        q: "Why are my Calathea's leaves going crispy at the edges?",
        a: "Crispy edges mean the air is too dry or there's mineral build-up from tap water. Calatheas are humidity-lovers — a bright bathroom is perfect, or group plants together. Water with filtered or rainwater if you can; Irish rainwater is free and ideal.",
      },
      {
        q: "Why does my Calathea move its leaves up and down?",
        a: "That's normal and a good sign. Calatheas are 'prayer plants' — they raise their leaves at night and lower them by day on a daily rhythm. A moving plant is a healthy, happy plant.",
      },
    ],
  },
  {
    id: "irish-tree-fern",
    slug: "irish-tree-fern",
    plantName: "Hardy Tree Fern",
    summary:
      "Dicksonia antarctica is one of the few genuinely exotic plants that overwinters outdoors in Ireland — here's how.",
    stats: { light: "Dappled shade", water: "Keep crown damp", hardiness: "Hardy to about -5°C (protect crown)", toxicity: "Pet safe" },
    faq: [
      {
        q: "Can a tree fern survive an Irish winter outdoors?",
        a: "Yes, in most of Ireland — the mild, damp maritime climate suits them better than most of Britain. In a sheltered, shady spot they overwinter outdoors. In a hard frost, protect the crown by stuffing it with straw and wrapping the trunk with fleece or hessian.",
      },
      {
        q: "How do you water a tree fern?",
        a: "Water the trunk and crown, not just the soil — the roots run up the fibrous trunk. In a dry spell keep the crown damp. Given the Irish climate, you'll rarely need to in winter, but a summer dry spell calls for a daily soak of the trunk.",
      },
      {
        q: "Where should I plant a tree fern in Ireland?",
        a: "A sheltered, shaded corner out of cold drying winds, with damp, humus-rich soil. The west and south coasts are ideal; inland midlands gardens work too with winter crown protection.",
      },
    ],
  },
  {
    id: "fatsia-japonica",
    slug: "fatsia-japonica",
    plantName: "Glossy Fatsia",
    summary:
      "Exotic looks, bomb-proof constitution — why Fatsia japonica is the smartest hardy exotic for a shady Irish garden.",
    stats: { light: "Shade to part shade", water: "Average, don't dry out", hardiness: "Hardy to about -10°C", toxicity: "Pet safe" },
    faq: [
      {
        q: "Is Fatsia japonica fully hardy in Ireland?",
        a: "Yes — it's reliably hardy across Ireland and keeps its big glossy leaves through winter, giving you an exotic, almost tropical look in the coldest, dampest months when everything else has died back.",
      },
      {
        q: "Will Fatsia grow in deep shade?",
        a: "It's one of the best exotic-looking shrubs for shade. It'll take a dry shady corner most plants refuse, though it prefers soil that doesn't bake dry in summer.",
      },
      {
        q: "Is Fatsia japonica safe for pets?",
        a: "Yes, it's considered non-toxic — a rare combination of dramatic foliage, full hardiness and pet safety.",
      },
    ],
  },
];

export const getCareGuide = (id) =>
  CARE_GUIDES.find((g) => g.id === id || g.slug === id);
